import { Box } from '@jotta/ui/Box'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import { Grid } from '@jotta/ui/Grid'
import { ProgressBar } from '@jotta/ui/ProgressBar'
import type { VirtualListItemFunctionComponent } from '@jotta/virtualized'
import { t } from '@lingui/macro'
import { observer } from 'mobx-react-lite'
import { useCallback } from 'react'
import type { FileUploadAction } from '../ActionManager/FileUploadAction'
import { FileSizeFragment } from '../FileSize/FileSize'
import SvgX from '@jotta/ui/SvgX'

function getDisplayStatus(upload: FileUploadAction) {
  switch (upload.status) {
    case 'error':
      return t`Error`
    case 'idle':
      return ''
    case 'success':
      return t`Done`
    case 'pending':
      if (upload.isPreparing) {
        return t`Preparing`
      } else if (upload.isUploading) {
        return t`Uploading`
      }
      break

    default:
      return ''
  }
}

export const FileActionQueueDetailsItem: VirtualListItemFunctionComponent<FileUploadAction> =
  observer(function FileActionQueueDetailsItem({ item: upload }) {
    const isPreparing = upload.isPreparing
    const showBytes = upload.isUploading || upload.status === 'success'

    const abort = useCallback(() => {
      upload.abort()
      upload.remove()
    }, [upload])

    return (
      <Grid
        px="4"
        columns="clamp(25%, var(--filename-col-width), 60%) 5em 10em 1fr 24px"
        key={`upload-${upload.id}`}
        sx={{
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {upload.name}
        </Box>
        <Box
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {getDisplayStatus(upload)}
        </Box>
        <Grid
          sx={{ visibility: showBytes ? 'visible' : 'hidden' }}
          gap={2}
          columns="1fr 1em 1fr"
        >
          <span sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
            <FileSizeFragment bytes={upload.completed} />
          </span>
          <span>/</span>
          <span sx={{ whiteSpace: 'nowrap' }}>
            <FileSizeFragment bytes={upload.total} />
          </span>
        </Grid>
        {isPreparing ? (
          <ProgressBar
            total={upload.md5Total}
            completed={upload.md5Completed}
          />
        ) : (
          <ProgressBar total={upload.total} completed={upload.completed} />
        )}
        <button
          sx={{ width: '24px', height: '24px', '& svg:': { color: 'text' } }}
          onClick={abort}
        >
          <PlainBrandIcon icon={SvgX} />
        </button>
      </Grid>
    )
  })
